import { Helmet } from 'react-helmet-async'
import AdventureCard from '@/components/AdventureCard'
import AdventureCategories from '@/components/AdventureCategories'
import HeroActivityRotator from '@/components/HeroActivityRotator'
import SmartImage from '@/components/SmartImage'
import HomePartnersCard from '@/components/HomePartnersCard'
import TestimonialsSection from '@/components/TestimonialsSection'
import TourCalendarModal from '@/components/TourCalendarModal'
import TourSchedulePreview from '@/components/TourSchedulePreview'
import { BlogCard } from '@/features/blog/components'
import { supabase } from '@/lib/supabase'
import { CACHE_TIMES } from '@/lib/queryClient'
import { Activity } from '@/types'
import { activityImageAlt } from '@/utils/seo'
import { useQuery } from '@tanstack/react-query'
import { addDays, format } from 'date-fns'
import { motion, useSpring } from 'framer-motion'
import {
  ArrowRight,
  Award,
  BookOpen,
  Calendar,
  Clock,
  MapPin,
  Mountain,
  Shield,
  Star,
  Users,
} from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { localizeRows, I18N_FIELDS } from '@/i18n/localize'
import { activityPath } from '@/hooks/useNavActivities'
import { useOptimizedScroll } from '../lib/effects/useOptimizedScroll'

const Home: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false)
  const { scrollYProgress } = useOptimizedScroll({
    target: containerRef,
  })

  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  const [, setPrefersDarkMode] = useState(false)

  // Check user preferences
  useEffect(() => {
    const mediaQueryMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    )
    const mediaQueryDark = window.matchMedia('(prefers-color-scheme: dark)')

    setPrefersReducedMotion(mediaQueryMotion.matches)
    setPrefersDarkMode(mediaQueryDark.matches)

    const handleMotionChange = (e: MediaQueryListEvent) =>
      setPrefersReducedMotion(e.matches)
    const handleDarkChange = (e: MediaQueryListEvent) =>
      setPrefersDarkMode(e.matches)

    mediaQueryMotion.addEventListener('change', handleMotionChange)
    mediaQueryDark.addEventListener('change', handleDarkChange)

    return () => {
      mediaQueryMotion.removeEventListener('change', handleMotionChange)
      mediaQueryDark.removeEventListener('change', handleDarkChange)
    }
  }, [])

  // Smooth scroll progress for effects
  useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  })

  const { data: guides } = useQuery({
    queryKey: ['guides', 'count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('guides')
        .select('*', { count: 'exact', head: true })

      if (error) throw error
      return count || 0
    },
    ...CACHE_TIMES.STATIC,
  })

  // Fetch homepage sections visibility settings
  const { data: homepageSections } = useQuery({
    queryKey: ['site-settings', 'homepage_sections'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'homepage_sections')
        .single()

      if (error) {
        console.error('Error fetching homepage sections:', error)
        // Return default values if setting doesn't exist
        return {
          hero: true,
          activities: true,
          features: true,
          tours_schedule: true,
          motorbike_rentals: true,
          commitment: true,
          blog: true,
          testimonials: true,
          cta: true,
        }
      }
      return data.value as {
        hero: boolean
        activities: boolean
        features: boolean
        tours_schedule: boolean
        motorbike_rentals: boolean
        commitment: boolean
        blog: boolean
        testimonials: boolean
        cta: boolean
      }
    },
    ...CACHE_TIMES.DYNAMIC,
  })

  const { t, i18n } = useTranslation('home')
  // Fetch active activities
  const { data: activities } = useQuery({
    queryKey: ['activities', 'home', i18n.language],
    queryFn: async () => {
      // Note: images contains base64 data - consider migrating to image server for better performance
      const { data, error } = await supabase
        .from('activities')
        .select(
          'id, title, slug, description, activity_type, duration_minutes, price_usd, max_capacity, features, location, meeting_point, is_active, display_order, flight_options, images, i18n'
        )
        .eq('is_active', true)
        // trekking and rock climbing are sold by their own family card higher
        // up the page; this grid is the rest of the line-up.
        .not('activity_type', 'in', '("trekking","rock_climbing")')
        .order('display_order', { ascending: true })

      if (error) throw error
      return localizeRows(
        data as Activity[],
        i18n.language,
        I18N_FIELDS.activities
      )
    },
    ...CACHE_TIMES.STATIC,
  })

  // Paramotor operational status — drives "Coming Back Soon" treatment.
  // value is jsonb in site_settings; defaults to 'live' if row missing or fetch fails.
  const { data: paramotorStatus } = useQuery({
    queryKey: ['site-settings', 'paramotor_status'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'paramotor_status')
        .maybeSingle()
      if (error) {
        console.error('Failed to fetch paramotor_status', error)
        return 'live' as const
      }
      const v = data?.value
      return (typeof v === 'string' ? v : 'live') as 'paused' | 'live'
    },
    ...CACHE_TIMES.STATIC,
  })

  // Check if there are any scheduled tours in next 14 days
  const { data: hasScheduledTours } = useQuery({
    queryKey: ['homepage-has-scheduled-tours'],
    queryFn: async () => {
      const today = new Date()
      const fourteenDaysLater = addDays(today, 14)

      const { data, error } = await supabase
        .from('tour_instances')
        .select('id, current_bookings, max_capacity')
        .gte('date', format(today, 'yyyy-MM-dd'))
        .lte('date', format(fourteenDaysLater, 'yyyy-MM-dd'))
        .eq('status', 'scheduled')
        .limit(1)

      if (error) throw error

      // Check if any tour has available spots
      const availableTours = data?.filter((instance) => {
        const currentBookings = instance.current_bookings || 0
        const maxCapacity = instance.max_capacity || 0
        return currentBookings < maxCapacity
      })

      return availableTours && availableTours.length > 0
    },
    ...CACHE_TIMES.DYNAMIC,
  })

  // Fetch recent blog posts with tags and category
  const { data: blogPosts } = useQuery({
    queryKey: ['blog', 'home'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select(
          `
          id,
          slug,
          status,
          published_at,
          read_time_minutes,
          featured_image_id,
          category:blog_categories(id, slug, translation:blog_category_translations!inner(name, language_code)),
          content:blog_content!inner(title, excerpt, language_code),
          media:blog_media(id, storage_path, alt_text),
          tags:blog_post_tags(tag:blog_tags(id, translation:blog_tag_translations!inner(name, language_code)))
        `
        )
        .eq('status', 'published')
        .eq('content.language_code', 'en')
        .order('published_at', { ascending: false })
        .limit(3)

      if (error) {
        console.error('Blog posts fetch error:', error)
        return [] // Return empty array on error instead of throwing
      }

      // Transform data to match BlogCard expected structure
      return data?.map((post) => {
        // Handle category - can be array or object
        const categoryData = Array.isArray(post.category)
          ? post.category[0]
          : post.category

        return {
          ...post,
          category: categoryData
            ? {
                ...categoryData,
                translation: Array.isArray(categoryData.translation)
                  ? categoryData.translation[0]
                  : categoryData.translation,
              }
            : null,
          content: Array.isArray(post.content) ? post.content[0] : post.content,
          media: Array.isArray(post.media) ? post.media[0] : post.media,
          tags:
            post.tags?.map((t: any) => ({
              id: t.tag.id,
              translation: Array.isArray(t.tag.translation)
                ? t.tag.translation[0]
                : t.tag.translation,
            })) || [],
        } as any // Type assertion to satisfy BlogCard
      })
    },
    ...CACHE_TIMES.SEMI_STATIC,
  })

  const features = [
    {
      icon: Star,
      title: t('features.tailored.title'),
      description: t('features.tailored.description'),
    },
    {
      icon: Shield,
      title: t('features.safety.title'),
      description: t('features.safety.description'),
    },
    {
      icon: Mountain,
      title: t('features.guides.title'),
      description: t('features.guides.description'),
    },
    {
      icon: Award,
      title: t('features.level.title'),
      description: t('features.level.description'),
    },
    {
      icon: MapPin,
      title: t('features.access.title'),
      description: t('features.access.description'),
    },
    {
      icon: Clock,
      title: t('features.immersion.title'),
      description: t('features.immersion.description'),
    },
  ]

  return (
    <div
      ref={containerRef}
      className="relative"
      style={{ position: 'relative' }}
    >
      <Helmet>
        <title>
          Vang Vieng Tours & Activities | Premium Tour Operator | Terra Lao
        </title>
        <meta
          name="description"
          content="Terra Lao, the premium tour operator in Vang Vieng, Laos. Guided day tours and adventures: rock climbing, trekking, paramotor, hot-air balloon, Nam Ngum cruises and electric enduro. Book direct with a local operator."
        />
        <link rel="canonical" href="https://terralao.la/" />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Vang Vieng Tours & Activities | Premium Tour Operator | Terra Lao"
        />
        <meta
          property="og:description"
          content="Terra Lao, the premium tour operator in Vang Vieng, Laos. Guided day tours and adventures: rock climbing, trekking, paramotor, hot-air balloon, Nam Ngum cruises and electric enduro."
        />
        <meta property="og:url" content="https://terralao.la/" />
        <meta
          property="og:image"
          content="https://terralao.la/og-preview-optimized.jpg"
        />
        <meta property="og:site_name" content="Terra Lao Adventure" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vang Vieng Tours & Activities | Premium Tour Operator | Terra Lao"
        />
        <meta
          name="twitter:description"
          content="Terra Lao, the premium tour operator in Vang Vieng, Laos. Guided day tours and adventures: rock climbing, trekking, paramotor, balloon, cruises and enduro."
        />
        <meta
          name="twitter:image"
          content="https://terralao.la/og-preview-optimized.jpg"
        />
        {/* FAQPage schema lives in index.html (operator-level Q&As). Don't duplicate here. */}
      </Helmet>

      {/* Hero Section with Garage Laotien Theme */}
      <section className="relative min-h-dvh-screen overflow-hidden bg-garage-dark">
        {/* Single operator hero card: the premium tour operator SEO H1 and
            primary CTA stay in the first-paint HTML. See HeroActivityRotator. */}
        <HeroActivityRotator />

        {/* Garage Decorative Elements */}
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-garage-dark to-transparent" />
        <div className="absolute top-1/4 right-8 w-24 h-24 bg-garage-metal opacity-30 rounded-full blur-xl" />
        <div className="absolute bottom-1/4 left-12 w-16 h-16 bg-garage-red opacity-20 rounded-full blur-lg" />
      </section>

      {/* One card per family (enduro / trek / climbing) — each opens the page
          that actually compares and sells that family's options. */}
      <AdventureCategories />

      {/* Other Vang Vieng Adventures (paramotor temporarily paused) */}
      {homepageSections?.activities !== false && (
        <section
          id="other-adventures"
          className="py-16 bg-gradient-to-br from-garage-dark via-garage-metal to-garage-dust relative overflow-hidden"
        >
          {/* Background Effects */}
          <div className="absolute inset-0 bg-garage-texture opacity-10" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(139,69,19,0.1)_0%,transparent_70%)]" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* No section header: "Choose Your Adventure" above already
                introduces the line-up, and the cards say what they are. */}
            {/* Activity Preview Cards - Database Driven */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {activities && activities.length > 0
                ? activities.map((activity, index) => {
                    const isParamotor =
                      activity.activity_type === 'paramotor' ||
                      activity.slug === 'paramotor-tandem-flight'
                    const isParamotorPaused =
                      isParamotor && paramotorStatus === 'paused'
                    const image = activity.images?.[0]
                    const imageNode = image ? (
                      <SmartImage
                        src={image}
                        alt={activityImageAlt(activity.title, {
                          activityType: activity.activity_type,
                          location: activity.location,
                        })}
                        settings={activity.image_settings?.[image]}
                        className={`h-full w-full transition-transform duration-300 group-hover:scale-105 ${
                          isParamotorPaused ? 'grayscale' : ''
                        }`}
                        loading="lazy"
                      />
                    ) : undefined

                    // Paused: same card, but it collects an email instead of
                    // sending anyone to a page they cannot book.
                    if (isParamotorPaused) {
                      return (
                        <AdventureCard
                          key={activity.id}
                          href="mailto:terralaoadventure@gmail.com?subject=Notify%20me%20when%20paramotor%20is%20back&body=Hi%2C%20please%20notify%20me%20when%20paramotor%20tandem%20flights%20are%20bookable%20again%20at%20Terra%20Lao%20Adventure.%20Thanks%21"
                          title={activity.title}
                          body={t('activityCard.paramotorPaused', {
                            defaultValue:
                              'Paramotor flights are paused while we upgrade the equipment. Leave your email and we will tell you when bookings reopen.',
                          })}
                          cta={t('activityCard.notifyMe', {
                            defaultValue: 'Notify me',
                          })}
                          badge={t('activityCard.comingBack', {
                            defaultValue: 'Coming back soon',
                          })}
                          imageNode={imageNode}
                          muted
                          index={index}
                          reduceMotion={prefersReducedMotion}
                        />
                      )
                    }

                    return (
                      <AdventureCard
                        key={activity.id}
                        to={activityPath(activity)}
                        title={activity.title}
                        body={activity.description}
                        cta={t('activityCard.cta', {
                          defaultValue: 'See details',
                        })}
                        priceFrom={activity.price_usd}
                        priceNote={
                          activity.activity_type === 'tuktuk'
                            ? t('activityCard.perVehicle', {
                                defaultValue: '/ tuk tuk',
                              })
                            : undefined
                        }
                        fromLabel={t('categories.from', 'From')}
                        imageNode={imageNode}
                        index={index}
                        reduceMotion={prefersReducedMotion}
                      />
                    )
                  })
                : // Fallback to placeholder cards if no activities
                  [
                    {
                      icon: '🚤',
                      title: 'River Cruises',
                      desc: 'Sunset boat rides',
                      color: 'from-blue-600 to-cyan-600',
                    },
                    {
                      icon: '🎈',
                      title: 'Hot Air Balloon',
                      desc: 'Sunrise flights',
                      color: 'from-orange-600 to-yellow-600',
                    },
                    {
                      icon: '🪂',
                      title: 'Paramotor',
                      desc: 'Powered aerial tours',
                      color: 'from-purple-600 to-pink-600',
                    },
                  ].map((fallback, index) => (
                    <motion.div
                      key={fallback.title}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{
                        delay: prefersReducedMotion ? 0 : index * 0.1,
                        duration: prefersReducedMotion ? 0.3 : 0.6,
                      }}
                      className="group bg-garage-card/50 backdrop-blur-xl rounded-2xl p-6 border border-garage-border opacity-50 flex flex-col h-full"
                    >
                      <div
                        className={`w-16 h-16 bg-gradient-to-br ${fallback.color} rounded-xl flex items-center justify-center text-3xl mb-4`}
                      >
                        {fallback.icon}
                      </div>
                      <h3 className="text-xl font-bold text-garage-cream mb-2 font-garage-title">
                        {fallback.title}
                      </h3>
                      <p className="text-garage-cream/70 text-sm">
                        {fallback.desc}
                      </p>
                    </motion.div>
                  ))}
              {/* Trek card removed from this 3-up activities grid — the
                  dedicated "Lost Valley Treks" comparison section above
                  is the home page's trek surface. */}
            </div>

            {/* ponytail: the "Explore All Adventures" CTA pointed at the old
                /beyond-enduro page — every activity now lives in this very
                section, so the button had nowhere left to go. */}
          </div>
        </section>
      )}

      {/* Features Section with Garage Workshop Theme */}
      {homepageSections?.features !== false && (
        <section className="py-24 bg-garage-surface relative overflow-hidden">
          {/* Background Texture */}
          <div className="absolute inset-0 bg-garage-workshop-texture opacity-5" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.2 : 0.4 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-garage-dark mb-4 font-garage-title">
                {t('features.heading')}
              </h2>
              <p className="text-lg text-garage-text max-w-2xl mx-auto">
                {t('features.body')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    delay: prefersReducedMotion ? 0 : index * 0.1,
                    duration: prefersReducedMotion ? 0.4 : 0.6,
                  }}
                  className="text-center group bg-garage-card hover:bg-garage-card-hover rounded-xl p-6 transition-all duration-200 border border-garage-border hover:border-garage-accent shadow-garage hover:shadow-garage-hover"
                >
                  <div className="bg-garage-icon-bg w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-garage-accent group-hover:scale-110 transition-all duration-150">
                    <feature.icon className="h-8 w-8 text-garage-icon group-hover:text-garage-cream" />
                  </div>
                  <h3 className="text-xl font-semibold text-garage-dark mb-2 font-garage-title">
                    {feature.title}
                  </h3>
                  <p className="text-garage-text">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Tours Schedule Section - Only show if there are scheduled tours */}
      {homepageSections?.tours_schedule !== false && hasScheduledTours && (
        <section className="py-20 bg-garage-surface relative overflow-hidden">
          {/* Background Texture */}
          <div className="absolute inset-0 bg-garage-workshop-texture opacity-5" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.2 : 0.4 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-garage-dark mb-4 font-garage-title">
                {t('tourSchedule.heading')}
              </h2>
              <p className="text-lg text-garage-text max-w-3xl mx-auto">
                {t('tourSchedule.body')}
              </p>
              <div className="flex items-center justify-center gap-4 mt-6">
                <div className="flex items-center gap-2 text-sm text-garage-text/70">
                  <Calendar className="h-4 w-4 text-garage-accent" />
                  <span>{t('tourSchedule.liveAvailability')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-garage-text/70">
                  <Users className="h-4 w-4 text-garage-accent" />
                  <span>{t('tourSchedule.maxRiders')}</span>
                </div>
              </div>
            </motion.div>

            <TourSchedulePreview prefersReducedMotion={prefersReducedMotion} />

            {/* CTA to see all tours */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                delay: prefersReducedMotion ? 0 : 0.2,
                duration: 0.3,
              }}
              className="text-center mt-12"
            >
              <div className="bg-garage-card rounded-xl p-6 border border-garage-border">
                <p className="text-garage-text mb-4">
                  {t('tourSchedule.customDates')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => setIsCalendarModalOpen(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-garage-accent hover:bg-garage-primary text-garage-cream font-medium rounded-lg transition-all duration-150 hover:scale-105 transform"
                  >
                    <Calendar className="h-4 w-4" />
                    {t('tourSchedule.viewAllTours')}
                  </button>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-garage-card border-2 border-garage-accent text-garage-accent hover:bg-garage-accent hover:text-garage-cream font-medium rounded-lg transition-all duration-150"
                  >
                    <Users className="h-4 w-4" />
                    {t('tourSchedule.requestCustom')}
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Motorbike Rentals Mention */}
      {homepageSections?.motorbike_rentals !== false && (
        <section className="py-16 bg-garage-surface relative overflow-hidden">
          <div className="absolute inset-0 bg-garage-workshop-texture opacity-5" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.2 : 0.4 }}
              className="max-w-3xl mx-auto text-center"
            >
              <div className="bg-garage-card rounded-2xl p-8 md:p-12 border-2 border-garage-border hover:border-garage-accent transition-all duration-200">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-garage-accent/10 border border-garage-accent/30 rounded-full mb-4">
                  <span className="text-garage-accent text-sm font-bold">
                    {t('rentals.badge')}
                  </span>
                </div>

                <h3 className="text-3xl md:text-4xl font-bold text-garage-dark mb-4 font-garage-title">
                  {t('rentals.heading')}
                </h3>

                {/* Motorcycle Image */}
                <div className="mb-6 flex justify-center">
                  <img
                    src="/images/tawang-motorcycle.png"
                    alt="Tawang Semi-Automatic Motorcycle"
                    className="w-full max-w-md h-auto"
                  />
                </div>

                <div className="bg-garage-surface/50 rounded-lg p-4 mb-6 border border-garage-border inline-block">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-garage-accent/10 rounded-full flex items-center justify-center">
                      <MapPin className="h-6 w-6 text-garage-accent" />
                    </div>
                    <div className="text-left">
                      <h4 className="font-bold text-garage-dark mb-1">
                        {t('rentals.bikeName')}
                      </h4>
                      <p className="text-sm text-garage-text/70">
                        {t('rentals.bikeDesc')}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <Link
                    to="/self-guided-routes"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-garage-accent hover:bg-garage-primary text-garage-cream font-bold rounded-lg transition-all duration-150 hover:scale-105 transform"
                  >
                    <MapPin className="h-4 w-4" />
                    {t('rentals.viewRouteMaps')}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Commitment Section with Garage Workshop Theme */}
      {homepageSections?.commitment !== false && (
        <section className="py-20 bg-gradient-to-br from-garage-dark via-garage-metal to-garage-accent text-garage-cream relative overflow-hidden">
          {/* Workshop Texture Overlay */}
          <div className="absolute inset-0 bg-garage-workshop-texture opacity-10" />
          <div className="absolute inset-0 bg-black/20" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.2 : 0.4 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4 font-garage-title">
                {t('commitment.heading')}
              </h2>
              <p className="text-garage-cream/80 text-lg max-w-2xl mx-auto">
                {t('commitment.body')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              {[
                {
                  number: guides || 0,
                  label: t('commitment.guidesLabel'),
                  quote: t('commitment.guidesQuote'),
                },
                {
                  number: '15+',
                  label: t('commitment.yearsLabel'),
                  quote: t('commitment.yearsQuote'),
                },
                {
                  number: '100%',
                  label: t('commitment.safetyLabel'),
                  quote: t('commitment.safetyQuote'),
                },
                {
                  number: '∞',
                  label: t('commitment.potentialLabel'),
                  quote: t('commitment.potentialQuote'),
                },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    delay: prefersReducedMotion ? 0 : index * 0.1,
                    duration: prefersReducedMotion ? 0.4 : 0.6,
                  }}
                  className="bg-garage-card/20 backdrop-blur-sm rounded-xl p-6 hover:bg-garage-card/30 transition-all duration-150 border border-garage-border/30 hover:border-garage-accent/50"
                >
                  <div className="text-4xl md:text-5xl font-bold text-garage-accent mb-3 font-garage-title">
                    {stat.number}
                  </div>
                  <div className="text-garage-cream font-medium text-lg mb-3">
                    {stat.label}
                  </div>
                  <div className="text-garage-cream/70 text-sm italic border-t border-garage-border/30 pt-3">
                    {stat.quote}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{
                delay: prefersReducedMotion ? 0 : 0.25,
                duration: 0.2,
              }}
              className="flex flex-wrap justify-center items-center gap-8 mt-16 text-sm text-garage-cream/70"
            >
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-garage-accent" />
                <span>{t('commitment.licensed')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-garage-accent" />
                <span>{t('commitment.equipment')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-garage-accent" />
                <span>{t('commitment.smallGroups')}</span>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Blog Section - Knowledge Sharing */}
      {homepageSections?.blog !== false && (
        <section className="py-20 bg-gradient-to-br from-garage-surface to-garage-mud relative overflow-hidden">
          {/* Background Texture */}
          <div className="absolute inset-0 bg-garage-workshop-texture opacity-5" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.2 : 0.4 }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-garage-accent/20 border border-garage-accent/30 rounded-full mb-6">
                <BookOpen className="h-5 w-5 text-garage-accent" />
                <span className="text-garage-dark font-medium">
                  {t('blog.badge')}
                </span>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-garage-dark mb-4 font-garage-title">
                {t('blog.heading')}
              </h2>
              <p className="text-lg text-garage-text max-w-2xl mx-auto leading-relaxed">
                {t('blog.body')}
              </p>
            </motion.div>

            {/* Blog Posts Grid */}
            {blogPosts && blogPosts.length > 0 ? (
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                {blogPosts.map((post, index) => (
                  <motion.div
                    key={post.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: prefersReducedMotion ? 0 : index * 0.1,
                      duration: prefersReducedMotion ? 0.4 : 0.6,
                    }}
                  >
                    <BlogCard post={post} />
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-garage-text/70">Blog posts coming soon...</p>
              </div>
            )}

            {/* CTA to Blog */}
            {blogPosts && blogPosts.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  delay: prefersReducedMotion ? 0 : 0.3,
                  duration: 0.3,
                }}
                className="text-center"
              >
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-garage-accent hover:bg-garage-primary text-garage-cream font-bold text-lg rounded-xl shadow-garage hover:shadow-garage-hover transition-all duration-150 hover:scale-105 transform border-2 border-garage-accent/50"
                >
                  <BookOpen className="h-5 w-5" />
                  <span>Explore All Travel Guides</span>
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* Adventure Photo Gallery */}
      {/* <AdventureCarousel /> */}

      {/* Testimonials Section - Google Reviews */}
      {homepageSections?.testimonials !== false && (
        <TestimonialsSection prefersReducedMotion={prefersReducedMotion} />
      )}

      {/* Enduro FAQ — visible HTML mirror of FAQPage schema (AI Overviews extract from visible text) */}
      <section
        id="enduro-faq"
        className="py-20 bg-garage-surface relative overflow-hidden"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="text-3xl md:text-4xl font-black text-garage-dark mb-3 text-center font-garage-title"
          >
            {t('faq.heading')}
          </motion.h2>
          <p className="text-base text-garage-text/80 text-center mb-10 max-w-2xl mx-auto">
            {t('faq.subtitle')}{' '}
            <a href="/contact" className="text-garage-accent underline">
              {t('faq.messageUs')}
            </a>
            .
          </p>

          <div className="space-y-3">
            {(
              t('faq.items', { returnObjects: true }) as Array<{
                q: string
                a: string
              }>
            ).map((item) => (
              <details
                key={item.q}
                className="group bg-white rounded-xl border-2 border-garage-border hover:border-garage-accent/60 transition-colors p-5 open:border-garage-accent open:shadow-garage"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none gap-4">
                  <h3 className="text-base sm:text-lg font-bold text-garage-dark group-open:text-garage-accent transition-colors">
                    {item.q}
                  </h3>
                  <span className="text-garage-accent text-2xl leading-none flex-shrink-0 group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm sm:text-base text-garage-text leading-relaxed">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Garage Workshop Finale */}
      {homepageSections?.cta !== false && (
        <section className="py-24 bg-gradient-to-br from-garage-surface via-garage-mud to-garage-dark relative overflow-hidden">
          {/* Tire Track Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-25"
            style={{
              backgroundImage: `url('/images/tire-track-bg.webp')`,
            }}
          />

          {/* Workshop Final Texture */}
          <div className="absolute inset-0 bg-garage-workshop-final-texture opacity-8" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(139,69,19,0.05)_0%,transparent_25%)] pointer-events-none" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(45,80,22,0.04)_0%,transparent_25%)] pointer-events-none" />

          <div className="max-w-5xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative bg-white/5 backdrop-blur-md rounded-3xl p-6 sm:p-8 border border-white/10 shadow-2xl"
            >
              {/* Floating elements */}
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-16 h-1 bg-gradient-to-r from-garage-accent to-garage-primary rounded-full" />

              <motion.h2
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: prefersReducedMotion ? 0 : 0.2 }}
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-garage-cream mb-6 bg-gradient-to-r from-garage-cream via-garage-accent to-garage-primary bg-clip-text text-transparent font-garage-title"
              >
                {t('cta.heading')}
              </motion.h2>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: prefersReducedMotion ? 0 : 0.4 }}
                className="text-xl text-garage-cream font-bold mb-8 max-w-3xl mx-auto leading-relaxed drop-shadow-garage"
              >
                {t('cta.body')}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: prefersReducedMotion ? 0 : 0.3 }}
                className="flex justify-center"
              >
                <motion.div
                  whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="relative group"
                >
                  <Link
                    to="/tours"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-garage-primary to-garage-accent text-garage-cream font-bold text-lg rounded-lg shadow-garage hover:shadow-garage-hover transition-all duration-150 border-2 border-garage-primary hover:border-garage-accent"
                  >
                    <span>{t('cta.button')}</span>
                    <Star className="w-5 h-5" />
                  </Link>
                </motion.div>
              </motion.div>

              {/* Google Maps Preview */}
              <motion.div
                id="find-us"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: prefersReducedMotion ? 0 : 0.4 }}
                className="mt-8 max-w-4xl mx-auto"
              >
                <div className="relative rounded-xl overflow-hidden border-2 border-garage-accent/30 shadow-2xl">
                  <iframe
                    src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=Terra+Lao+Adventure,Vang+Vieng,Laos&zoom=15&maptype=roadmap"
                    width="100%"
                    height="200"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full"
                  ></iframe>

                  {/* Floating Button Inside Map */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
                    <a
                      href="https://maps.app.goo.gl/AcV6Wq24J44b4GxT6"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-garage-dark/70 backdrop-blur-md px-6 py-3 rounded-lg border border-garage-accent/50 text-garage-cream font-bold text-sm hover:bg-garage-accent hover:border-garage-accent transition-all duration-150 inline-flex items-center gap-2 shadow-lg hover:shadow-xl"
                    >
                      <MapPin className="w-4 h-4" />
                      Open in Google Maps
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* Location indicator */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: prefersReducedMotion ? 0 : 1 }}
                className="mt-4 flex items-center justify-center"
              >
                <a
                  href="https://maps.app.goo.gl/AcV6Wq24J44b4GxT6"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-garage-dark/80 backdrop-blur-sm px-4 py-3 rounded-lg border border-garage-accent/30 hover:bg-garage-accent/20 transition-all duration-150"
                >
                  <MapPin className="w-5 h-5 text-garage-accent" />
                  <span className="text-garage-cream font-bold">
                    Vang Vieng, Laos
                  </span>
                </a>
              </motion.div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Partners — single hero card just above the footer */}
      <HomePartnersCard />

      {/* Tour Calendar Modal */}
      <TourCalendarModal
        isOpen={isCalendarModalOpen}
        onClose={() => setIsCalendarModalOpen(false)}
        prefersReducedMotion={prefersReducedMotion}
      />
    </div>
  )
}

export default Home
